// The minimum-version gate on tablets (plan §9.5, D-042). A Capacitor app can't update
// itself: when the server says this version is too old, the station offers to open the store
// (Android: Google Play; iPad: TestFlight until the App Store listing exists). The notice
// under the top bar stays either way; work carries on and waits on the device.
// Electron handles the gate in its main process (auto-update), a browser only shows the notice.
//
// Store links: VITE_ANDROID_UPDATE_URL / VITE_IOS_UPDATE_URL at build time. The defaults are
// placeholders until the listings exist: Play's page for the brand's application id, and the
// TestFlight app.

import type { StationData, UpgradeState } from '@enp-suite/data';
import native from '@enp/brand/native.json';

export type NativePlatform = 'android' | 'ios';

export function updateUrl(
  platform: NativePlatform,
  env: { VITE_ANDROID_UPDATE_URL?: string; VITE_IOS_UPDATE_URL?: string } = import.meta.env,
): string {
  if (platform === 'android') {
    return env.VITE_ANDROID_UPDATE_URL || `market://details?id=${native.androidApplicationId}`;
  }
  return env.VITE_IOS_UPDATE_URL || 'itms-beta://';
}

export function updateQuestion(upgrade: UpgradeState): string {
  const version = upgrade.minAppVersion ? ` (version ${upgrade.minAppVersion} or later)` : '';
  return (
    `This station app needs an update to keep syncing${version}. ` +
    'Work carries on and is kept on this device. Open the store to update it now?'
  );
}

export interface GatePromptOptions {
  platform: NativePlatform;
  /** Asks yes/no (window.confirm: a native dialog in Capacitor's web view). */
  confirm: (question: string) => boolean;
  /** Leaves the app for the store (Capacitor hands non-app URLs to the OS). */
  open: (url: string) => void;
}

/** Asks once per gate (per minimum version) while the app runs. Returns the unsubscribe. */
export function promptOnUpgradeGate(data: StationData, options: GatePromptOptions): () => void {
  let asked: string | undefined;
  const check = () => {
    const { upgrade } = data.status.get();
    if (!upgrade.required) {
      asked = undefined;
      return;
    }
    const key = upgrade.minAppVersion ?? '';
    if (asked === key) return;
    asked = key;
    if (options.confirm(updateQuestion(upgrade))) options.open(updateUrl(options.platform));
  };
  const unsubscribe = data.status.subscribe(check);
  check();
  return unsubscribe;
}

interface CapacitorGlobal {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
}

/** The tablet platform this app runs on natively, or undefined (Electron, a browser). */
export function nativePlatform(): NativePlatform | undefined {
  if (typeof window === 'undefined') return undefined;
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  if (cap?.isNativePlatform?.() !== true) return undefined;
  return cap.getPlatform?.() === 'ios' ? 'ios' : 'android';
}

/** Whether "Open the store" makes sense here (a tablet; Electron updates itself). */
export function canOpenStore(): boolean {
  return nativePlatform() !== undefined;
}

// Verified in Capacitor 8's sources: navigating the web view to a URL outside the app opens
// it with the OS (Android: an ACTION_VIEW intent; iOS: UIApplication.open).
const leaveFor = (url: string) => window.location.assign(url);

/** Opens the store page of this app (the update notice's button on tablets). */
export function openStore(): void {
  const platform = nativePlatform();
  if (platform) leaveFor(updateUrl(platform));
}

/** Wires the prompt on Android and iPadOS; does nothing elsewhere. */
export function startAppUpdatePrompt(data: StationData): void {
  const platform = nativePlatform();
  if (!platform) return;
  promptOnUpgradeGate(data, {
    platform,
    confirm: (question) => window.confirm(question),
    open: leaveFor,
  });
}
